import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { api, ApiError } from '../../api/client.js';
import { DataTable } from '../../components/DataTable.js';
import { Alert, Button, Card, Select, Spinner } from '../../components/ui.js';
import { dateTime, integer, money } from '../../format.js';
import {
  compareInteger,
  compareNumericString,
  compareText,
  sortingFnFor,
} from '../../lib/table-sort.js';

/**
 * "Compare a filled sheet" — a stopgap for the 2026 holiday order, kept in this
 * one file (with the API's routes/booking-compare.ts) so it is easy to delete.
 *
 * The owner uploads a copy of the booking workbook he filled by hand; the API
 * reads his quantities out of each line's own cell and compares them, line by
 * line, with what the current purchase plan would write into the same cells.
 * This card shows the totals against the budget, the counts per group, the
 * grouped table with its flags, the reader's warnings, a CSV for the agent, and
 * a button that adopts his quantities into the plan as overrides. See
 * docs/bookings.md, "Compare a filled sheet".
 */

// ---------------------------------------------------------------------------
// The API's shapes (local on purpose: this card is meant to be removable)
// ---------------------------------------------------------------------------

type CompareGroup = 'agree' | 'owner_higher' | 'owner_lower' | 'owner_only' | 'app_only';

type FlagCode =
  'pack_priced' | 'not_multiple' | 'not_orderable' | 'no_sku' | 'unreadable' | 'moved';

interface CompareFlag {
  code: FlagCode;
  label: string;
}

interface CompareRow {
  lineId: number;
  tab: string;
  cell: string;
  category: string;
  itemCode: string;
  model: string;
  color: string | null;
  sizeLabel: string | null;
  window: string;
  availability: string;
  skuLabel: string;
  ownerQty: number;
  movedIn: number;
  appQty: number;
  delta: number;
  unitCost: string | null;
  ownerCost: string | null;
  appCost: string | null;
  map: string | null;
  mapPackQty: number | null;
  group: CompareGroup;
  planNote: string;
  flags: CompareFlag[];
}

interface NotApplied {
  lineId: number;
  tab: string;
  cell: string;
  itemCode: string;
  ownerQty: number;
  reason: string;
}

interface Comparison {
  rows: CompareRow[];
  counts: Record<CompareGroup, number> & { bothZero: number };
  totals: {
    ownerUnits: number;
    appUnits: number;
    ownerCost: string;
    appCost: string;
    ownerUncostedUnits: number;
    appUncostedUnits: number;
    budget: string | null;
    ownerOverBudget: boolean;
  };
  tabs: { tab: string; ownerUnits: number; appUnits: number; ownerCost: string; appCost: string }[];
  sheetId: string;
  file: { name: string; storedAt: string };
  warnings: string[];
  notes: string[];
  plan: {
    id: number | null;
    name: string | null;
    status: 'pending' | 'sent' | null;
    canApply: boolean;
    notice: string | null;
  };
  applyPreview: {
    applied: number;
    moved: number;
    zeroed: number;
    notApplied: NotApplied[];
    excludedSkus: string[];
    budget: string | null;
  };
}

interface ApplyResult {
  planName: string;
  applied: number;
  moved: number;
  zeroed: number;
  notApplied: NotApplied[];
  excludedSkus: string[];
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

const GROUPS: { id: CompareGroup; label: string; hint: string }[] = [
  { id: 'agree', label: 'Agree', hint: 'Your quantity equals the plan’s.' },
  { id: 'owner_higher', label: 'Owner higher', hint: 'You typed more than the plan orders.' },
  { id: 'owner_lower', label: 'Owner lower', hint: 'You typed less than the plan orders.' },
  {
    id: 'owner_only',
    label: 'Owner only',
    hint: 'You typed a quantity; the plan orders none here.',
  },
  { id: 'app_only', label: 'App only', hint: 'The plan orders here; your cell is blank or 0.' },
];
const GROUP_ORDER = new Map(GROUPS.map((g, i) => [g.id, i]));
const GROUP_LABEL = new Map(GROUPS.map((g) => [g.id, g.label]));

const WINDOW_LABELS: Record<string, string> = {
  ship_1: '1st ship',
  ship_2: '2nd ship',
  ship_3: '3rd ship',
  q2: 'Q2',
  q3: 'Q3',
  q4: 'Q4',
};

const FLAG_STYLE: Record<FlagCode, string> = {
  pack_priced: 'bg-warn-bg text-warn-text',
  not_multiple: 'bg-warn-bg text-warn-text',
  not_orderable: 'bg-danger-bg text-danger-text',
  no_sku: 'bg-danger-bg text-danger-text',
  unreadable: 'bg-danger-bg text-danger-text',
  moved: 'bg-brand-600/15 text-brand-600',
};

/** The short chip text; the full label rides in the chip's tooltip. */
const FLAG_CHIP: Record<FlagCode, string> = {
  pack_priced: 'per pack',
  not_multiple: 'not a multiple',
  not_orderable: 'not ordered',
  no_sku: 'no SKU',
  unreadable: 'unreadable',
  moved: 'moved',
};

function signed(n: number): string {
  return n > 0 ? `+${integer(n)}` : integer(n);
}

// ---------------------------------------------------------------------------
// The table
// ---------------------------------------------------------------------------

const columnHelper = createColumnHelper<CompareRow>();

// The columns an owner reads first — the cell, what he typed against the plan,
// why, and the flags — come first, so they fit at 1440 px; the costs follow.
const COLUMNS = [
  columnHelper.accessor((r) => GROUP_ORDER.get(r.group) ?? 0, {
    id: 'group',
    header: 'Group',
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => <span className="whitespace-nowrap">{GROUP_LABEL.get(c.row.original.group)}</span>,
  }),
  columnHelper.accessor('tab', {
    header: 'Tab / cell',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => (
      <span
        className="block max-w-40 truncate text-xs"
        title={`${c.getValue()} ${c.row.original.cell}`}
      >
        <span className="font-mono text-muted">{c.row.original.cell}</span> {c.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor('model', {
    header: 'Item',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => {
      const r = c.row.original;
      const detail = [r.itemCode, r.color, r.sizeLabel].filter(Boolean).join(' · ');
      return (
        <span className="block max-w-56 truncate" title={`${r.model} — ${detail}`}>
          {r.model} <span className="text-xs text-muted">{detail}</span>
        </span>
      );
    },
  }),
  columnHelper.accessor('ownerQty', {
    header: 'Owner',
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => (
      <span className="whitespace-nowrap">
        <span className="font-medium text-strong">{integer(c.getValue())}</span>
        {c.row.original.movedIn > 0 && (
          <span className="ml-1 text-xs text-brand-600" title="Moved here on apply">
            +{integer(c.row.original.movedIn)}
          </span>
        )}
      </span>
    ),
  }),
  columnHelper.accessor('appQty', {
    header: 'App',
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('delta', {
    header: 'Delta',
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => signed(c.getValue()),
  }),
  columnHelper.accessor('planNote', {
    header: 'Reason',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => (
      <span className="block max-w-56 text-xs leading-tight text-muted">{c.getValue()}</span>
    ),
  }),
  columnHelper.accessor((r) => r.flags.length, {
    id: 'flags',
    header: 'Flags',
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => (
      <span className="flex max-w-48 flex-wrap gap-1">
        {c.row.original.flags.map((f) => (
          <span
            key={f.code}
            className={`whitespace-nowrap rounded px-1.5 text-[11px] leading-5 ${FLAG_STYLE[f.code]}`}
            title={f.label}
          >
            {FLAG_CHIP[f.code]}
          </span>
        ))}
      </span>
    ),
  }),
  columnHelper.accessor('window', {
    header: 'Window',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => (
      <span className="whitespace-nowrap">{WINDOW_LABELS[c.getValue()] ?? c.getValue()}</span>
    ),
  }),
  columnHelper.accessor('skuLabel', {
    header: 'Matched SKU',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => <span className="whitespace-nowrap text-xs">{c.getValue()}</span>,
  }),
  columnHelper.accessor((r) => r.unitCost ?? undefined, {
    id: 'unitCost',
    header: 'Unit cost',
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => money(c.getValue() ?? null),
  }),
  columnHelper.accessor((r) => r.ownerCost ?? undefined, {
    id: 'ownerCost',
    header: 'Owner cost',
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => money(c.getValue() ?? null),
  }),
  columnHelper.accessor((r) => r.appCost ?? undefined, {
    id: 'appCost',
    header: 'App cost',
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => money(c.getValue() ?? null),
  }),
  columnHelper.accessor((r) => r.map ?? undefined, {
    id: 'map',
    header: 'MAP',
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => (
      <span>
        {money(c.getValue() ?? null)}
        {c.row.original.mapPackQty !== null && c.row.original.mapPackQty > 1 && (
          <span className="ml-1 text-xs text-muted">/{c.row.original.mapPackQty}</span>
        )}
      </span>
    ),
  }),
];

// ---------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------

export function CompareFilledSheet({
  bookingId,
  canWrite,
}: {
  bookingId: number;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [groupFilter, setGroupFilter] = useState<CompareGroup | ''>('');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [applied, setApplied] = useState<ApplyResult | null>(null);
  const key = ['booking-compare', bookingId];

  const comparison = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ comparison: Comparison | null }>(`/api/bookings/${bookingId}/compare`),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return api.upload<{ comparison: Comparison }>(`/api/bookings/${bookingId}/compare`, form);
    },
    onSuccess: (result) => {
      setApplied(null);
      setConfirming(false);
      queryClient.setQueryData(key, result);
    },
  });

  // Apply names the exact sheet on screen, so a newer upload elsewhere can never
  // be applied in its place.
  const apply = useMutation({
    mutationFn: (sheetId: string) =>
      api.post<ApplyResult>(`/api/bookings/${bookingId}/compare/apply`, { sheetId }),
    onSuccess: (result) => {
      setApplied(result);
      setConfirming(false);
      // The plan's overrides changed: every plan-driven view (and this comparison) is stale.
      void queryClient.invalidateQueries();
    },
  });

  const data = comparison.data?.comparison ?? null;
  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    return all.filter(
      (r) =>
        (groupFilter === '' || r.group === groupFilter) && (!flaggedOnly || r.flags.length > 0),
    );
  }, [data, groupFilter, flaggedOnly]);

  function submit(files: FileList | null) {
    const file = files?.[0];
    if (file) upload.mutate(file);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <Card className="p-4" testId="compare-filled-sheet">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-strong">Compare a filled sheet</h2>
          <p className="text-sm text-muted">
            Upload your hand-filled copy of this workbook to see it line by line against the
            purchase plan. A stopgap for the 2026 holiday order.
          </p>
        </div>
        {canWrite && (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => inputRef.current?.click()}
              disabled={upload.isPending}
            >
              {upload.isPending
                ? 'Comparing…'
                : data
                  ? 'Upload a newer copy'
                  : 'Upload filled sheet'}
            </Button>
            <input
              ref={inputRef}
              type="file"
              aria-label="Filled booking workbook"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => submit(e.target.files)}
            />
          </div>
        )}
      </div>

      {upload.isError && (
        <div className="mt-3">
          <Alert>
            {upload.error instanceof ApiError ? upload.error.message : 'Upload failed.'}
          </Alert>
        </div>
      )}
      {comparison.isPending && (
        <div className="mt-3">
          <Spinner label="Loading the comparison" />
        </div>
      )}
      {comparison.isError && (
        <div className="mt-3">
          <Alert>{comparison.error.message}</Alert>
        </div>
      )}
      {comparison.data && !data && (
        <p className="mt-3 text-sm text-muted">No filled sheet uploaded for this booking yet.</p>
      )}

      {data && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-muted">
            {data.file.name} · compared {dateTime(data.file.storedAt)}
            {data.plan.name ? ` · against plan “${data.plan.name}”` : ''}
          </p>

          {data.plan.notice && <Alert tone="info">{data.plan.notice}</Alert>}

          {/* The summary bar: owner vs app vs budget, then the group counts. */}
          <div className="grid gap-3 sm:grid-cols-3">
            <SummaryTile
              label="Your sheet"
              value={money(data.totals.ownerCost)}
              detail={`${integer(data.totals.ownerUnits)} units${
                data.totals.ownerUncostedUnits > 0
                  ? ` · ${integer(data.totals.ownerUncostedUnits)} uncosted`
                  : ''
              }`}
              warn={data.totals.ownerOverBudget}
            />
            <SummaryTile
              label="The plan"
              value={money(data.totals.appCost)}
              detail={`${integer(data.totals.appUnits)} units${
                data.totals.appUncostedUnits > 0
                  ? ` · ${integer(data.totals.appUncostedUnits)} uncosted`
                  : ''
              }`}
            />
            <SummaryTile
              label="Budget"
              value={data.totals.budget === null ? 'None set' : money(data.totals.budget)}
              detail={
                data.totals.ownerOverBudget ? 'Your sheet is over the budget' : 'From the plan'
              }
              warn={data.totals.ownerOverBudget}
            />
          </div>

          <div className="flex flex-wrap gap-2" role="group" aria-label="Lines per group">
            {GROUPS.map((g) => (
              <button
                key={g.id}
                type="button"
                title={g.hint}
                onClick={() => setGroupFilter((current) => (current === g.id ? '' : g.id))}
                className={`rounded-md px-2.5 py-1 text-sm ring-1 ring-inset ${
                  groupFilter === g.id
                    ? 'bg-brand-600 text-brand-on ring-brand-600'
                    : 'bg-surface text-body ring-line-strong hover:bg-sunken'
                }`}
              >
                {g.label}{' '}
                <span className="font-semibold" data-testid={`compare-count-${g.id}`}>
                  {integer(data.counts[g.id])}
                </span>
              </button>
            ))}
            <span className="self-center text-xs text-muted">
              · {integer(data.counts.bothZero)} lines blank on both sides
            </span>
          </div>

          {data.warnings.length > 0 && (
            <Alert tone="warning">
              <span className="font-medium">
                Cells that could not be read as quantities (not applied)
              </span>
              <ul className="mt-1 list-disc pl-5" data-testid="compare-warnings">
                {data.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </Alert>
          )}
          {data.notes.length > 0 && (
            <details className="text-sm text-muted" data-testid="compare-notes">
              <summary className="cursor-pointer">
                {integer(data.notes.length)} cell{data.notes.length === 1 ? '' : 's'} read as 0 from
                a dash or “n/a”
              </summary>
              <ul className="mt-1 list-disc pl-5">
                {data.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            </details>
          )}

          <details className="text-sm">
            <summary className="cursor-pointer text-muted">Subtotals per tab</summary>
            <table className="mt-2 text-sm">
              <thead className="text-left text-xs text-muted">
                <tr>
                  <th className="pr-4 font-medium">Tab</th>
                  <th className="pr-4 text-right font-medium">Your units</th>
                  <th className="pr-4 text-right font-medium">Your cost</th>
                  <th className="pr-4 text-right font-medium">Plan units</th>
                  <th className="text-right font-medium">Plan cost</th>
                </tr>
              </thead>
              <tbody>
                {data.tabs.map((t) => (
                  <tr key={t.tab}>
                    <td className="pr-4">{t.tab}</td>
                    <td className="tabular pr-4 text-right">{integer(t.ownerUnits)}</td>
                    <td className="tabular pr-4 text-right">{money(t.ownerCost)}</td>
                    <td className="tabular pr-4 text-right">{integer(t.appUnits)}</td>
                    <td className="tabular text-right">{money(t.appCost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href={`/api/bookings/${bookingId}/compare.csv?sheetId=${data.sheetId}`}
              className="text-sm text-brand-600 hover:underline"
            >
              Download CSV
            </a>
            {canWrite && !confirming && (
              <Button
                onClick={() => setConfirming(true)}
                disabled={!data.plan.canApply || apply.isPending}
                title={data.plan.canApply ? undefined : (data.plan.notice ?? undefined)}
              >
                Apply sheet quantities to the plan
              </Button>
            )}
          </div>

          {canWrite && confirming && (
            <ApplyConfirm
              planName={data.plan.name ?? ''}
              preview={data.applyPreview}
              pending={apply.isPending}
              onConfirm={() => apply.mutate(data.sheetId)}
              onCancel={() => setConfirming(false)}
            />
          )}

          {apply.isError && (
            <Alert>{apply.error instanceof ApiError ? apply.error.message : 'Apply failed.'}</Alert>
          )}
          {applied && (
            <Alert tone="success">
              Applied {integer(applied.applied)} quantit{applied.applied === 1 ? 'y' : 'ies'} to “
              {applied.planName}”
              {applied.moved > 0 ? `, ${integer(applied.moved)} moved from another cell` : ''}
              {applied.zeroed > 0 ? `, ${integer(applied.zeroed)} set to 0` : ''}.
              {applied.excludedSkus.length > 0 &&
                ` Not ordered by the plan (excluded from reorder, or no longer listed on Amazon): ${applied.excludedSkus.join(', ')}.`}
              {applied.notApplied.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer">
                    {integer(applied.notApplied.length)} not applied
                  </summary>
                  <ul className="mt-1 list-disc pl-5">
                    {applied.notApplied.map((n) => (
                      <li key={n.lineId}>
                        {n.tab} {n.cell} ({n.itemCode}): {n.reason}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </Alert>
          )}

          <div className="rounded-md border border-line">
            <DataTable
              data={rows}
              columns={COLUMNS}
              initialSorting={[{ id: 'group', desc: false }]}
              storageKey="booking-compare"
              resetKey={`${groupFilter}|${String(flaggedOnly)}`}
              getRowId={(r) => String(r.lineId)}
              dense
              emptyText="No lines in this view."
              toolbar={
                <>
                  <Select
                    aria-label="Group"
                    value={groupFilter}
                    onChange={(e) => setGroupFilter(e.target.value as CompareGroup | '')}
                    className="w-40"
                  >
                    <option value="">All groups</option>
                    {GROUPS.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.label}
                      </option>
                    ))}
                  </Select>
                  <label className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={flaggedOnly}
                      onChange={(e) => setFlaggedOnly(e.target.checked)}
                    />
                    Flagged only
                  </label>
                  <span>{integer(rows.length)} lines</span>
                </>
              }
            />
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * The confirm before Apply writes anything: what it will do, counted from the
 * comparison the server already computed (no write), and the budget caveat.
 */
function ApplyConfirm({
  planName,
  preview,
  pending,
  onConfirm,
  onCancel,
}: {
  planName: string;
  preview: Comparison['applyPreview'];
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="space-y-2 rounded-md p-3 text-sm ring-1 ring-inset ring-line-strong"
      data-testid="compare-apply-confirm"
    >
      <p className="font-medium text-strong">Apply this sheet to “{planName}”?</p>
      <ul className="list-disc pl-5">
        <li>
          <span data-testid="compare-preview-applied">{integer(preview.applied)}</span> line
          {preview.applied === 1 ? '' : 's'} will be set as overrides
        </li>
        <li>
          <span data-testid="compare-preview-moved">{integer(preview.moved)}</span> of them carry a
          quantity moved from another cell of the same SKU
        </li>
        <li>
          <span data-testid="compare-preview-zeroed">{integer(preview.zeroed)}</span> will be set to
          0 (blank on the sheet where the plan orders)
        </li>
        <li>
          <span data-testid="compare-preview-not-applied">
            {integer(preview.notApplied.length)}
          </span>{' '}
          will not be applied
          {preview.notApplied.length > 0 && (
            <ul className="mt-1 list-[circle] pl-5 text-xs text-muted">
              {preview.notApplied.map((n) => (
                <li key={n.lineId}>
                  {n.tab} {n.cell} ({n.itemCode}): {n.reason}
                </li>
              ))}
            </ul>
          )}
        </li>
        {preview.excludedSkus.length > 0 && (
          <li>
            Not ordered by the plan (excluded from reorder, or no longer listed on Amazon):{' '}
            {preview.excludedSkus.join(', ')}
          </li>
        )}
      </ul>
      {preview.budget !== null && (
        <Alert tone="warning">
          A budget of {money(preview.budget)} is set: the plan will still cut applied quantities to
          it; clear or raise the budget to order exactly the sheet.
        </Alert>
      )}
      <div className="flex gap-2">
        <Button onClick={onConfirm} disabled={pending}>
          {pending ? 'Applying…' : 'Yes, apply'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function SummaryTile({
  label,
  value,
  detail,
  warn = false,
}: {
  label: string;
  value: string;
  detail: string;
  warn?: boolean;
}) {
  return (
    <div className={`rounded-md p-3 ring-1 ring-inset ${warn ? 'ring-warn-line' : 'ring-line'}`}>
      <p className="text-xs text-muted">{label}</p>
      <p className="tabular text-lg font-semibold text-strong">{value}</p>
      <p className={`text-xs ${warn ? 'text-warn-text' : 'text-muted'}`}>{detail}</p>
    </div>
  );
}
